import React from "react";

// A ChunkLoadError happens when the browser asks for a webpack chunk whose
// hash no longer exists — typically because a fresh build replaced the
// bundle while the user had the page open. The fix is just to reload, but
// we guard against an infinite reload loop in case the chunk is genuinely
// missing (network down, CDN broken, bad deploy).
const CHUNK_RELOAD_FLAG = "foundry_chunk_reload_attempt";
const CHUNK_RELOAD_MAX = 2; // total reloads before we give up and show UI

function isChunkLoadError(err) {
  if (!err) return false;
  const name = err.name || "";
  const msg = err.message || "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Loading CSS chunk [\w-]+ failed/i.test(msg)
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Auto-recover from stale chunks before we even render the fallback UI.
    if (isChunkLoadError(error) && typeof window !== "undefined") {
      try {
        const attempts = parseInt(sessionStorage.getItem(CHUNK_RELOAD_FLAG) || "0", 10);
        if (attempts < CHUNK_RELOAD_MAX) {
          sessionStorage.setItem(CHUNK_RELOAD_FLAG, String(attempts + 1));
          // Force a fresh fetch (bypasses HTTP cache) so we pick up the
          // new index.html with the current chunk hashes.
          window.location.reload();
          return { hasError: true, error, recovering: true };
        }
      } catch {
        /* sessionStorage unavailable — fall through to UI */
      }
    }
    return { hasError: true, error };
  }

  componentDidMount() {
    // If we got here without a chunk error, the previous reload (if any)
    // succeeded; clear the counter so future stale chunks get fresh attempts.
    try {
      if (typeof sessionStorage !== "undefined" && !this.state.hasError) {
        sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
      }
    } catch { /* ignore */ }
  }

  componentDidCatch(error, errorInfo) {
    if (isChunkLoadError(error)) {
      console.warn("[ErrorBoundary] ChunkLoadError caught — attempting reload:", error.message);
    } else {
      console.error("[ErrorBoundary] Uncaught error:", error, errorInfo);
    }
  }

  handleReload = () => {
    try { sessionStorage.removeItem(CHUNK_RELOAD_FLAG); } catch { /* ignore */ }
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleHome = () => {
    try { sessionStorage.removeItem(CHUNK_RELOAD_FLAG); } catch { /* ignore */ }
    this.setState({ hasError: false, error: null });
    window.location.href = "/";
  };

  render() {
    // While the auto-reload is in flight, render nothing — the page is
    // about to be replaced by the new bundle anyway. Showing the "Something
    // went wrong" UI for a split second was confusing users.
    if (this.state.hasError && this.state.recovering) {
      return null;
    }
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="max-w-md w-full bg-card border border-border rounded-lg shadow-sm p-6">
            <h1 className="text-xl font-semibold mb-2">
              {isChunkLoadError(this.state.error) ? "Page assets out of date" : "Something went wrong"}
            </h1>
            <p className="text-sm text-muted-foreground mb-4">
              {isChunkLoadError(this.state.error)
                ? "We tried to reload automatically but the page is still failing to fetch its assets. Check your network connection, then click Reload. If you're on a flaky connection, try again in a moment."
                : "The page hit an unexpected error. Your data is safe — you can reload or go home."}
            </p>
            {this.state.error?.message && (
              <pre className="text-xs bg-muted text-muted-foreground p-2 rounded mb-4 overflow-auto max-h-40 whitespace-pre-wrap">
                {this.state.error.message}
              </pre>
            )}
            <div className="flex gap-2">
              <button
                onClick={this.handleReload}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:opacity-90"
              >
                Reload page
              </button>
              <button
                onClick={this.handleHome}
                className="flex-1 px-4 py-2 bg-muted text-foreground rounded-md text-sm hover:opacity-90"
              >
                Go home
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
